/**
 * DrawDialog — the draw machine (effectué / partiel / échoué) + the redraw (repris) path.
 *
 *  - One outcome line per attached tube. `echoue` requires a motif (the config motifsEchec vocabulary).
 *  - Submit -> POST /sampling/tickets/:id/prelever (sampling.prelevement.create).
 *  - Redraw -> resolve the reception-reissued tube by matricule (POST /sampling/scan,
 *    sampling.prelevement.read) then POST /sampling/tickets/:id/reprendre (sampling.prelevement.create).
 *
 * Capability-gated: the dialog only opens behind canCreate; the redraw block additionally needs the
 * scan (read) capability. The server re-checks every request regardless.
 */
import { useMemo, useState } from "react";
import { Button, FormField, Input, Modal, Select, Textarea, useToast } from "@/ui";
import { useT } from "@/app";
import { useCan } from "@/auth";
import { token } from "@/design/theme";
import { type ApiError } from "@/api";
import { CAP } from "../capabilities";
import { usePrelever, useReprendre, useScan } from "../hooks";
import type { BoardTicket, DrawInput, TicketTube } from "../types";

/** The failed-draw motif vocabulary (mirror of config.disciplines.prelevement.motifsEchec). */
const MOTIFS_ECHEC = [
  "veine_introuvable",
  "patient_agite",
  "tube_casse",
  "volume_insuffisant",
  "hemolyse_visible",
  "refus_patient",
] as const;

type Outcome = "effectue" | "partiel" | "echoue";

interface RowState {
  statut: Outcome;
  volume: string;
  motif_echec: string;
  notes: string;
}

const emptyRow = (): RowState => ({ statut: "effectue", volume: "", motif_echec: "", notes: "" });

export interface DrawDialogProps {
  ticket: BoardTicket;
  open: boolean;
  onClose: () => void;
}

export function DrawDialog({ ticket, open, onClose }: DrawDialogProps) {
  const { t } = useT();
  const toast = useToast();
  const canScan = useCan(CAP.prelevementRead);

  const tubes: TicketTube[] = useMemo(
    () => (ticket.tubes ?? []).filter((tt) => tt.statut === "attache"),
    [ticket.tubes],
  );

  const [rows, setRows] = useState<Record<string, RowState>>({});
  const [redrawMatricule, setRedrawMatricule] = useState("");

  const prelever = usePrelever();
  const reprendre = useReprendre();
  const scan = useScan();

  const rowFor = (tubeId: string): RowState => rows[tubeId] ?? emptyRow();
  const patch = (tubeId: string, next: Partial<RowState>) =>
    setRows((r) => ({ ...r, [tubeId]: { ...rowFor(tubeId), ...next } }));

  const motifMissing = tubes.some((tt) => {
    const row = rowFor(tt.tube_id);
    return row.statut === "echoue" && !row.motif_echec;
  });

  const submitDraw = () => {
    if (motifMissing) {
      toast.error(t("sampling.draw.motifRequired"));
      return;
    }
    const draws: DrawInput[] = tubes.map((tt) => {
      const row = rowFor(tt.tube_id);
      const draw: DrawInput = { tube_id: tt.tube_id, statut: row.statut };
      if (row.volume.trim() !== "") {
        const v = Number(row.volume);
        if (Number.isFinite(v)) draw.volume = Math.trunc(v);
      }
      if (row.statut === "echoue" && row.motif_echec) draw.motif_echec = row.motif_echec;
      if (row.notes.trim() !== "") draw.notes = row.notes.trim();
      return draw;
    });

    prelever.mutate(
      { ticketId: ticket.id, draws },
      {
        onSuccess: () => {
          toast.success(t("sampling.draw.success", { n: ticket.numero }));
          setRows({});
          onClose();
        },
        onError: (e: ApiError) => toast.error(e.message, e.code),
      },
    );
  };

  const submitRedraw = () => {
    const matricule = redrawMatricule.trim();
    if (!matricule) return;
    // Resolve the reissued tube by matricule, then append a fresh Prelevement (reprise).
    scan.mutate(
      { matricule },
      {
        onSuccess: (tube) =>
          reprendre.mutate(
            { ticketId: ticket.id, tube_id: tube.id },
            {
              onSuccess: () => {
                toast.success(t("sampling.draw.redraw.success"));
                setRedrawMatricule("");
              },
              onError: (e: ApiError) => toast.error(e.message, e.code),
            },
          ),
        onError: () => toast.error(t("sampling.draw.redraw.resolveFail")),
      },
    );
  };

  const motifOptions = MOTIFS_ECHEC.map((m) => ({ value: m, label: t(`sampling.draw.motif.${m}`) }));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={t("sampling.draw.title", { n: ticket.numero })}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            {t("sampling.draw.cancel")}
          </Button>
          <Button
            variant="primary"
            onClick={submitDraw}
            loading={prelever.isPending}
            disabled={tubes.length === 0 || motifMissing}
          >
            {t("sampling.draw.submit")}
          </Button>
        </>
      }
    >
      <div className="smp-draw">
        {tubes.length === 0 ? (
          <p style={{ color: token.slate }}>{t("sampling.draw.noTubes")}</p>
        ) : (
          tubes.map((tt) => {
            const row = rowFor(tt.tube_id);
            return (
              <div className="smp-draw__tube" key={tt.tube_id}>
                <div className="smp-draw__tubehead">
                  <span className="nv-mono nv-ltr-island" style={{ fontWeight: 600 }}>
                    {tt.discipline_code || "—"}
                  </span>
                  <span className="nv-mono nv-ltr-island" style={{ color: token.steel }}>
                    #{tt.tube_id}
                  </span>
                </div>

                <FormField label={t("sampling.draw.outcome")}>
                  {(p) => (
                    <Select
                      {...p}
                      value={row.statut}
                      onChange={(e) => patch(tt.tube_id, { statut: e.target.value as Outcome })}
                      options={[
                        { value: "effectue", label: t("sampling.draw.outcome.effectue") },
                        { value: "partiel", label: t("sampling.draw.outcome.partiel") },
                        { value: "echoue", label: t("sampling.draw.outcome.echoue") },
                      ]}
                    />
                  )}
                </FormField>

                <div className="smp-draw__grid">
                  <FormField label={t("sampling.draw.volume")}>
                    {(p) => (
                      <Input
                        {...p}
                        mono
                        type="number"
                        inputMode="numeric"
                        min={0}
                        value={row.volume}
                        onChange={(e) => patch(tt.tube_id, { volume: e.target.value })}
                      />
                    )}
                  </FormField>

                  {row.statut === "echoue" && (
                    <FormField
                      label={t("sampling.draw.motif")}
                      required
                      error={!row.motif_echec ? t("sampling.draw.motifRequired") : undefined}
                    >
                      {(p) => (
                        <Select
                          {...p}
                          placeholder={t("sampling.draw.motif.placeholder")}
                          value={row.motif_echec}
                          onChange={(e) => patch(tt.tube_id, { motif_echec: e.target.value })}
                          options={motifOptions}
                        />
                      )}
                    </FormField>
                  )}
                </div>

                <FormField label={t("sampling.draw.notes")}>
                  {(p) => (
                    <Textarea
                      {...p}
                      rows={2}
                      value={row.notes}
                      onChange={(e) => patch(tt.tube_id, { notes: e.target.value })}
                    />
                  )}
                </FormField>
              </div>
            );
          })
        )}

        {/* Redraw (repris) — append-only on a reception-reissued tube. */}
        {canScan && (
          <div
            className="smp-draw__tube"
            style={{ borderStyle: "dashed" }}
          >
            <div className="smp-draw__tubehead">
              <strong>{t("sampling.draw.redraw")}</strong>
            </div>
            <p style={{ margin: 0, color: token.slate, fontSize: token.fsCaption }}>
              {t("sampling.draw.redrawHint")}
            </p>
            <div className="smp-scan__row">
              <FormField label={t("sampling.draw.redrawMatricule")} className="smp-grow">
                {(p) => (
                  <Input
                    {...p}
                    mono
                    value={redrawMatricule}
                    onChange={(e) => setRedrawMatricule(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault();
                        submitRedraw();
                      }
                    }}
                  />
                )}
              </FormField>
              <Button
                variant="secondary"
                onClick={submitRedraw}
                loading={scan.isPending || reprendre.isPending}
                disabled={!redrawMatricule.trim()}
              >
                {t("sampling.draw.redrawSubmit")}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Modal>
  );
}
